import { Fragment } from "react";
import { useLingui } from "@lingui/react/macro";
import { CheckSquare } from "lucide-react";
import { cn } from "@/lib/utils";
import { ingredientLineName } from "@/types/recipe";
import { useUoms } from "@/hooks/useUomReference";
import type { LineMeasures } from "@/lib/api/requests/recipes";
import { LineAllergens } from "../AllergenSymbol";
import { StepPhotos } from "../StepPhotos";
import { CcpLegend, CcpStepFlag, CcpStepPrintNote } from "../ccp";
import { methodHasCcp } from "@/lib/recipes/ccp";
import {
  cardEquivalent,
  kitchenAmount,
  multipleLabel,
  scaledYield,
  sheetScales,
  type BatchScale,
  type StagedMethod,
} from "@/lib/recipes/staged-method";

interface RecipeStagedSheetProps {
  method: StagedMethod;
  scale: BatchScale;
  /** The recipe's yield as written, for the column headings; null when it has none. */
  yieldQuantity: number | null | undefined;
  /** Each line's weight and volume, by ingredient line id, from the cost summary. */
  measures?: Map<number, LineMeasures | null>;
  /** The recipe, for the "Got it" on a critical control point. */
  recipeId?: number;
  /** The cooking session the sheet was opened from, when it was. */
  cookingSessionId?: number;
}

/**
 * 8a, the staged sheet: each stage's ingredients beside the steps that use
 * them, with two batch sizes side by side — half and full, or the recipe as
 * written and the scale picked — and the one being made in bold.
 */
export function RecipeStagedSheet({ method, scale, yieldQuantity, measures, recipeId, cookingSessionId }: RecipeStagedSheetProps) {
  const { t } = useLingui();
  const { data: uoms } = useUoms();
  const columns = sheetScales(scale);
  const heading = (size: BatchScale) => {
    const label = size === 0.5 ? t`Half` : size === 1 ? t`Full` : multipleLabel(size);
    const count = scaledYield(yieldQuantity, size);
    return count ? `${label} · ${count}` : label;
  };
  const qty = (quantity: number, unit: string, size: BatchScale) => kitchenAmount(Number(quantity) * size, unit);

  return (
    <>
    <div className="overflow-x-auto rounded-2xl border border-(--color-border) print:overflow-visible print:border-0">
      <div className="grid min-w-[48rem] grid-cols-[minmax(0,1fr)_8rem_8rem_minmax(0,1.4fr)] text-base">
        <HeadCell>{t`Ingredients`}</HeadCell>
        {columns.map((size) => (
          <HeadCell key={size} className={cn("pr-3 pl-0 text-right", scale === size && "text-(--color-text-primary)")}>
            {heading(size)}
          </HeadCell>
        ))}
        <HeadCell>{t`Procedure`}</HeadCell>

        {method.tasks.length > 0 && (
          <div className="col-span-4 flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-(--color-border) px-4 py-3">
            <span className="text-xs font-bold tracking-wide text-(--color-text-secondary) uppercase">{t`Before you start`}</span>
            {method.tasks.map((task) => (
              <span key={task.id} className="flex items-center gap-1.5 font-medium text-(--color-text-primary)">
                <CheckSquare className="size-4 text-(--color-text-secondary)" />
                {task.action}
                {task.detail && <span className="font-normal text-(--color-text-secondary)">· {task.detail}</span>}
              </span>
            ))}
          </div>
        )}

        {method.groups.map((group, gi) => (
          <Fragment key={group.stage?.id ?? `unstaged-${gi}`}>
            <div className="col-span-3 flex flex-col border-t border-(--color-border) py-2 pl-4">
              {group.lines.length === 0 ? (
                <span className="pr-4 text-(--color-text-secondary)">{t`No added ingredients`}</span>
              ) : (
                group.lines.map((line) => (
                  <div key={line.id} role="group" aria-label={ingredientLineName(line)} className="grid min-h-7.5 grid-cols-[minmax(0,1fr)_8rem_8rem] items-center">
                    <span className="min-w-0">
                      <span className="font-semibold text-(--color-text-primary)">{ingredientLineName(line)}</span>
                      {line.preparationNotes && (
                        <span className="text-(--color-text-secondary)"> {line.preparationNotes}</span>
                      )}{" "}
                      <LineAllergens line={line} />
                    </span>
                    {columns.map((size) => {
                      const equivalent = cardEquivalent(Number(line.quantity), line.unit, measures?.get(line.id), size, uoms);
                      return (
                        <span
                          key={size}
                          className={cn(
                            "flex flex-col items-end pr-3 text-right",
                            scale === size ? "font-bold text-(--color-text-primary)" : "text-(--color-text-secondary)",
                          )}
                        >
                          {/* The grid's column headers aren't announced per cell, so each
                              amount says which batch it is for. */}
                          <span className="whitespace-nowrap">
                            <span className="sr-only">{heading(size)}: </span>
                            {qty(line.quantity, line.unit, size)}
                          </span>
                          {equivalent && <span className="text-xs font-normal text-(--color-text-secondary)">{equivalent}</span>}
                        </span>
                      );
                    })}
                  </div>
                ))
              )}
            </div>
            <section
              aria-label={group.stage ? t`Procedure: ${group.stage.action}` : t`Procedure`}
              // The rule down the procedure's edge keeps the steps apart from
              // the amounts beside them, as the design draws it.
              className="flex flex-col gap-1.5 border-t border-l border-(--color-border) px-4 py-3"
            >
              {(group.stage || method.isStaged) && (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-(--color-surface-sidebar) px-2.5 py-0.5 text-xs font-bold tracking-wide text-(--color-text-on-dark) uppercase">
                    {group.stage?.action ?? t`Not staged yet`}
                  </span>
                  {group.stage?.detail && <span className="text-sm text-(--color-text-secondary)">{group.stage.detail}</span>}
                </div>
              )}
              {group.steps.map((step) => (
                <div key={step.number} className="grid grid-cols-[1.5rem_minmax(0,1fr)] text-[15px] leading-snug">
                  <span className="font-bold text-(--color-text-primary)">{step.number}.</span>
                  <span className="flex min-w-0 flex-col">
                    {step.isCcp ? (
                      <span className="flex items-start gap-2">
                        <span className="text-(--color-text-primary)">{step.text}</span>
                        <CcpStepFlag
                          recipeId={recipeId}
                          cookingSessionId={cookingSessionId}
                          number={step.number}
                          text={step.text}
                          stageName={group.stage?.action}
                        />
                      </span>
                    ) : (
                      <span className="text-(--color-text-primary)">{step.text}</span>
                    )}
                    <StepPhotos urls={step.mediaUrls} />
                    {step.isCcp && <CcpStepPrintNote text={step.text} className="mt-1" />}
                  </span>
                </div>
              ))}
            </section>
          </Fragment>
        ))}
      </div>
    </div>
    {methodHasCcp(method) && <CcpLegend />}
    </>
  );
}

function HeadCell({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        "bg-(--color-surface-muted) px-4 py-2.5 text-[13px] font-semibold tracking-wide text-(--color-text-secondary) uppercase",
        className,
      )}
    >
      {children}
    </div>
  );
}
